import React, { useState, useContext, useCallback } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import Modal, { ModalHeader, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import Heading from '@atlaskit/heading';
import { Grid, xcss, Flex, Box } from '@atlaskit/primitives';
import Button, { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import SectionMessage from '@atlaskit/section-message';

import { buildCreationLink } from '@confluence/create-blank-fabric-page';
import { useClassificationChangeState } from '@confluence/data-classification';
import { usePageState } from '@confluence/page-context';
import { useRouteActions } from '@confluence/route-manager';
import type { SelectedSpaceOnChangeProps } from '@confluence/space-selector';
import { SpaceSelector } from '@confluence/space-selector';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement';
import {
	ExperienceTrackerContext,
	WHITEBOARD_VERSION_HISTORY_RESTORE_EXPERIENCE,
} from '@confluence/experience-tracker';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';

import { useVersionHistoryAnalytics } from '../useVersionHistoryAnalytics';
import { getFormattedDate } from '../versionHistoryUtils';
import type { ContentVersionHistoryQuery_contentVersionHistory_nodes as VersionHistoryNode } from '../../graphql/__types__/ContentVersionHistoryQuery';

import { VersionTitleUpdate } from './VersionTitleUpdate';

const i18n = defineMessages({
	restoreContentTitle: {
		id: 'whiteboard-common.restore.content.title',
		defaultMessage: 'Restore as new whiteboard',
		description: 'Title for restore content modal',
	},
	closeIconButtonLabel: {
		id: 'whiteboard-common.close.icon.button.label',
		defaultMessage: 'Close icon',
		description: 'aria-label for close icon',
	},
	restoreButton: {
		id: 'whiteboard-common.restore.button',
		defaultMessage: 'Restore',
		description: 'Button text to restore content',
	},
	infoBoxText: {
		id: 'whiteboard-common.info.box.text',
		defaultMessage:
			'The new board will inherit the restriction and share settings of the original.',
		description:
			'Info message that indicates that the new board will inherit restrictions and share settings of the original',
	},
	saveWorkWithoutCreatingText: {
		id: 'whiteboard-common.save-work-without-creating.text',
		defaultMessage:
			'To save work without creating, try copying content from the whiteboard to an editable whiteboard.',
		description: 'Message informing users another way to saving work without creating',
	},
	spaceSelectorLabel: {
		id: 'whiteboard-common.space-selector.label',
		defaultMessage: 'Select space',
		description: 'Label for the space selector in the restore modal',
	},
	title: {
		id: 'whiteboard-common.title',
		defaultMessage: '[Restored] Untitled board {date}',
		description: 'Title name for the version selected to be restored as a new whiteboard',
	},
});

const gridStyles = xcss({
	width: '100%',
	alignItems: 'center',
});

const closeContainerStyles = xcss({
	gridArea: 'close',
});

const titleContainerStyles = xcss({
	gridArea: 'title',
});

const infoSectionStyles = xcss({
	marginTop: 'space.250',
});
const subInfoSectionStyles = xcss({
	marginTop: 'space.250',
	fontSize: '11px',
	fontWeight: '400',
});

type RestoreContentModalTypes = {
	onClose: () => void;
	selectedVersion: VersionHistoryNode;
};

export const RestoreContentModal = ({ onClose, selectedVersion }: RestoreContentModalTypes) => {
	const { formatMessage, formatDate, formatTime } = useIntl();
	const { onRestore } = useVersionHistoryAnalytics();
	const selectedVersionDate = getFormattedDate(selectedVersion.updatedTime, formatDate, formatTime);

	// use current space as default space key
	const [{ spaceKey, contentId }] = usePageState();
	const [destinationSpaceKey, setDestinationSpaceKey] = useState<string | undefined>(spaceKey);
	const [title, setTitle] = useState<string>(
		formatMessage(i18n.title, {
			date: selectedVersionDate,
		}),
	);
	const { push } = useRouteActions();
	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'restore-content-modal',
	});
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { selectedClassification, classificationError } = useClassificationChangeState(
		String(contentId),
		destinationSpaceKey,
		spaceKey,
	);
	const { hideSidePanel } = useSidePanelStateActions();

	const requiredSpaceOperations = [
		{
			targetType: 'page',
			operation: 'create',
		},
	];

	const onSpaceSelectorChange = (selectedSpace: SelectedSpaceOnChangeProps) => {
		const { spaceKey: selectedSpaceKey } = selectedSpace;
		setDestinationSpaceKey(selectedSpaceKey);
	};

	const handleRestoreClick = useCallback(() => {
		experienceTracker.start({
			name: WHITEBOARD_VERSION_HISTORY_RESTORE_EXPERIENCE,
		});
		const creationHref = buildCreationLink({
			contentType: 'whiteboard',
			source: 'versionHistoryRestore',
			restoreVersionContentId: selectedVersion.contentId,
			spaceKey: destinationSpaceKey,
			title,
		});
		onRestore(selectedVersion);
		hideSidePanel();
		push(creationHref);
	}, [
		experienceTracker,
		destinationSpaceKey,
		push,
		title,
		selectedVersion,
		onRestore,
		hideSidePanel,
	]);

	return (
		<Modal testId="restore-content-modal" width="400px">
			<ModalHeader>
				<Grid gap="space.200" templateAreas={['title close']} xcss={gridStyles}>
					<Flex justifyContent="start" xcss={titleContainerStyles}>
						<Heading size="medium" as="h6">
							<FormattedMessage {...i18n.restoreContentTitle} />
						</Heading>
					</Flex>
					<Flex justifyContent="end" xcss={closeContainerStyles}>
						<IconButton
							appearance="subtle"
							icon={CrossIcon}
							onClick={onClose}
							label={formatMessage(i18n.closeIconButtonLabel)}
						/>
					</Flex>
				</Grid>
			</ModalHeader>
			<ModalBody>
				<VersionTitleUpdate title={title} setTitle={setTitle} />
				<SpaceSelector
					onChange={onSpaceSelectorChange}
					operationsUsedToDetermineRestrictedStatus={requiredSpaceOperations}
					showRestrictedSpaces={false}
					filterDefaultFromResults={false}
					spaceKey={destinationSpaceKey}
					labelTextTransform="none"
					useMenuPortalTarget
					spaceClassification={selectedClassification}
					hasClassificationError={!!classificationError}
					selectStyleOverrides={{
						menuPortal: (base) => ({
							...base,
							zIndex: 9999,
						}),
					}}
				/>
				<Box xcss={infoSectionStyles}>
					<SectionMessage>
						<FormattedMessage {...i18n.infoBoxText} />
					</SectionMessage>
				</Box>
				<Box xcss={subInfoSectionStyles}>
					<FormattedMessage {...i18n.saveWorkWithoutCreatingText} />
				</Box>
			</ModalBody>
			<ModalFooter>
				<Button
					appearance="primary"
					onClick={enforceStorageLimit(handleRestoreClick)}
					testId="restore-content-button"
				>
					<FormattedMessage {...i18n.restoreButton} />
				</Button>
			</ModalFooter>
		</Modal>
	);
};
